/**
 * Folder-tree mutation helpers for the gallery slice.
 *
 * The gallery keeps folders as a TREE, not a flat list: a top-level folder is an
 * entry in `state.folders`, and a nested folder is an entry in its parent's
 * `items` array (see `addFolder`, which already recurses via `findFolderById`).
 *
 * Kept in its own module — one behaviour, one file, one test file — because the
 * bug this fixes (QA-51) was exactly a tree operation written as if the tree
 * were flat.
 */

/**
 * True when a node in an `items` array is a FOLDER rather than a wheel.
 *
 * `items` is heterogeneous: folders and wheels share it. A wheel carries
 * `wheel_segments` (this is the same discriminator FoldersV2/OwnerView.jsx uses
 * to split a level into `childFolders` / `childWheels`). Folder ids and wheel
 * ids come from different tables and CAN collide, so a folder removal must
 * never match a wheel.
 */
function isFolderNode(node) {
  return !!node && !node.wheel_segments;
}

/**
 * Remove the folder with `folderId` from a nested folder tree, at ANY depth.
 *
 * Mutates `list` in place (it is an immer draft inside the reducer) and returns
 * `true` if a folder was removed, `false` if no folder with that id exists
 * anywhere in the tree.
 *
 * QA-51: the previous implementation filtered only the top-level array, so a
 * successfully-deleted NESTED folder (server returned 200) stayed on screen.
 * The user then clicked Delete on that ghost tile, the second DELETE hit an
 * already-deleted id, the API answered 404, and the UI showed
 * "Failed to delete folder" — on a folder that was already gone.
 *
 * Ids are compared loosely on purpose: the API returns numbers, some call sites
 * hand through strings, and the reducer this replaces used `!=` for the same
 * reason.
 */
export function removeFolderById(list, folderId) {
  if (!Array.isArray(list)) return false;

  const index = list.findIndex(
    // eslint-disable-next-line eqeqeq
    (node) => isFolderNode(node) && node.id == folderId
  );
  if (index !== -1) {
    list.splice(index, 1);
    return true;
  }

  for (const node of list) {
    if (isFolderNode(node) && removeFolderById(node.items, folderId)) {
      return true;
    }
  }
  return false;
}

export default removeFolderById;
